import { useState, useEffect } from "react";
import {
  Button,
  TwModal as Modal,
  Text,
  Icon,
  Link,
} from "@virkesborsen/ui-component-library";
import { TailwindWrapper } from "../../../TailwindWrapper/TailwindWrapper.jsx";
import { useSubscription } from "../../context/SubscriptionCheckoutContext.js";
import {
  clearOrderFromCookie,
  trackSubscriptionCheckoutEvent,
} from "./utils.js";
import { formatPriceWithCurrency } from "../../utils.js";
import { ConfirmClosePopup } from "./components/ConfirmClosePopup.js";
import * as Steps from "./steps/index";
import { sendEvent } from "../../../../api";
import  {useModalCloseOnEsc}  from "../../utils.js";

export default function SubscriptionCheckout() {
  const {
    currentStep,
    isCheckoutOpen,
    nextStep,
    previousStep,
    selectedProperties,
    orderPrices,
    setErrorMessages,
    resetCheckout,
  } = useSubscription();

  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);

  const shouldHideNavigation = currentStep > 1;
  const shouldHideFooter = currentStep === 2;
  const hidePriceSummary = currentStep > 1 || !orderPrices.discountPrice;

  useEffect(() => {
    if (currentStep > 1) {
      setIsNextButtonDisabled(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (isCheckoutOpen && currentStep === 1) {
      sendEvent("plus_open_modal", { source: "plus" });
    } else if (isCheckoutOpen && currentStep === 2) {
      sendEvent("viewed_payment_plus", { source: "plus" });
    }
  }, [isCheckoutOpen, currentStep]);

  const handleClose = () => {
    setIsConfirmCloseOpen(false);
    clearOrderFromCookie();
    resetCheckout();
    trackSubscriptionCheckoutEvent("cancelled", currentStep);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleNextStep = () => {
    const hasErrors = selectedProperties.some((prop) => prop.plus_price_error);
    if (currentStep === 1 && hasErrors) {
      setErrorMessages((prev) => ({
        ...prev,
        properties: (
          <Text variant="text-xs/leading-normal/normal">
            Något gick fel med beräk ningen. Vi beklagar detta och ber dig att
            vänligen kontakta oss på{" "}
            <Link
              href="mailto:info@treebula.se"
              tw
              target="blank"
              underline
              className="text-danger-500"
            >
              info@treebula.se
            </Link>{" "}
            så återkommer vi till dig
          </Text>
        ),
      }));
      return;
    }

    if (currentStep === 1 && !selectedProperties.length) {
      setErrorMessages((prev) => ({
        ...prev,
        properties: "Du måste lägga till minst en fastighet för att forsätta",
      }));
      return;
    }

    return nextStep();
  };

  const handleDisableNextButton = (isEnabled) => {
    setIsNextButtonDisabled(isEnabled);
  };

  const getNavigationButtonLabel = () => {
    if (currentStep === 3) {
      return "Stäng";
    }

    return "Nästa steg";
  };

  const handleOpenDemoLink = () => {
    window.open(
      "https://2e1iip.share-eu1.hsforms.com/2XeAiDeueTBmhUZMmOkm-Mw",
      "_blank",
      "noopener,noreferrer"
    );
  };

  useModalCloseOnEsc(isCheckoutOpen, handleClose)

  if (currentStep === 2) {
    return (
      <>
        <Steps.Payment
          isOpen={isCheckoutOpen}
          handleClose={handleConfirmClose}
        />
        <TailwindWrapper>
          <ConfirmClosePopup
            isOpen={isConfirmCloseOpen}
            setIsOpen={setIsConfirmCloseOpen}
            handleClose={handleClose}
          />
        </TailwindWrapper>
      </>
    );
  }

  return (
    <TailwindWrapper>
      <Modal tw isOpen={isCheckoutOpen} borderless={currentStep === 1}>
        <Modal.Header tw className={shouldHideNavigation ? "hidden" : "px-4 py-3 md:p-6"}>
          <div className="flex w-full justify-between gap-4 items-start">
            <div className="flex flex-row gap-4 items-center">
              {currentStep !== 1 && (
                <Button
                  tw
                  variant="tertiary"
                  size="icon"
                  onClick={previousStep}
                >
                  <Icon
                    tw
                    name="ArrowRightSimple"
                    size="large"
                    className="rotate-180"
                  />
                </Button>
              )}
              <div className="flex flex-col gap-1">
                <Text variant="text-base/leading-normal/medium">
                  Bli Pluskund hos Treebula
                </Text>
              </div>
            </div>
            <Button
              tw
              variant="tertiary"
              size="icon"
              onClick={handleConfirmClose}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="large"
              />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Content tw>
          {currentStep === 1 && (
            <Steps.Order disableNextButton={handleDisableNextButton} />
          )}
          {/* 
            // Klarna HTML is rendered outside TailwindWrapper because Klarna Checkout cannot connect to its external API when inside a shadowRoot, which TailwindWrapper requires.
            which is used to isolate Tailwind styling here. 
          */}
          {currentStep === 3 && <Steps.ThankYou />}
        </Modal.Content>
        {!shouldHideFooter && (
          <Modal.Footer tw>
            <div
              className={[
                "flex flex-row justify-center items-center gap-2",
                hidePriceSummary ? "opacity-0" : "",
              ].join(" ")}
            >
              <Text variant="text-lg/leading-none/medium" className='whitespace-nowrap'>
                {formatPriceWithCurrency(orderPrices?.discountPrice)}
              </Text>
              <Text
                variant="text-sm/leading-normal/normal"
                className="text-charcoal-300"
              >
                exkl. moms
              </Text>
            </div>
            <div className="flex flex-row gap-4">
              <Button tw variant="secondary" onClick={handleOpenDemoLink}>
                Boka genomgång
              </Button>

              <Button
                tw
                variant="primary"
                onClick={handleNextStep}
                disabled={isNextButtonDisabled}
              >
                {getNavigationButtonLabel()}
              </Button>
            </div>
          </Modal.Footer>
        )}
      </Modal>
      <ConfirmClosePopup
        isOpen={isConfirmCloseOpen}
        setIsOpen={setIsConfirmCloseOpen}
        handleClose={handleClose}
      />
    </TailwindWrapper>
  );
}
